// Cell colour for the conditions table — and the contrast check that keeps the
// printed number readable on top of it.
//
// The design system reserves colour for DATA, and a heatmap of forecast values
// is data, so colour is allowed here. What is not allowed is colour carrying a
// signal on its own: every cell prints its number, so the fill answers "can you
// work" while the digits answer "what is it". Lose the colour to a photocopier,
// a projector or a colour-blind reader and the table still says everything it
// said before — it just says it more slowly.
//
// Two scales, and which one is in force is stated on the panel, never inferred:
//
//   LIMIT scale  — the cell is graded against the selected vessel+operation's
//                  own limit for that variable, through ops/workability.ts.
//                  This is the one the client asked for: the wave row can read
//                  green while the wind row reads red in the same column, so
//                  the binding constraint names itself.
//   ABSOLUTE scale — no vessel, no operation, or a variable with no limit
//                  (period, sea temperature, swell). A neutral ink ramp, scaled
//                  to the row's own maximum. It says "more" and "less" and
//                  deliberately does not say "good" and "bad", because without
//                  a limit there is nothing for it to be good or bad against.
//
// Every colour is resolved from the theme's CSS custom properties at DRAW time
// (the charts' rule) and then COMPOSITED over the surface here rather than
// painted at full strength. That is what keeps Dusk and Night from blowing out:
// the same weight over a dark ground yields a dark fill, and the ink chosen for
// it is whichever of the theme's two inks actually contrasts with the result.

import { WORK_GRADES } from "../ops/workability.js";

export type RGB = readonly [number, number, number];

/**
 * How strongly a grade's colour is mixed over the surface. Positional against
 * WORK_GRADES — index 0 is the best band — and graded so severity reads as
 * weight as well as hue, which is one more channel that survives greyscale.
 *
 * A test pins this array's length to WORK_GRADES'. Adding a fifth band without
 * a fifth weight would silently paint it at the No-go weight.
 */
export const GRADE_WEIGHTS: readonly number[] = [0.16, 0.32, 0.44, 0.58];

/** The neutral ramp's weight range: a floor so an all-but-empty row is still
 *  visibly a row, and a ceiling well short of solid so the number stays on top
 *  of the ink rather than fighting it. */
export const NEUTRAL_WEIGHT = { min: 0.05, max: 0.42 } as const;

/** Parse the CSS colour forms a custom property actually resolves to: #rgb,
 *  #rrggbb, rgb()/rgba(). Null for anything else (a named colour, a gradient),
 *  which callers treat as "skip the fill" rather than guessing. */
export function parseColor(css: string): RGB | null {
  const s = css.trim();
  if (s.startsWith("#")) {
    const hex = s.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return Number.isNaN(r + g + b) ? null : [r, g, b];
    }
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return Number.isNaN(r + g + b) ? null : [r, g, b];
    }
    return null;
  }
  const m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(s);
  if (m === null) return null;
  const rgb: RGB = [Number(m[1]), Number(m[2]), Number(m[3])];
  return rgb.every((v) => Number.isFinite(v)) ? rgb : null;
}

export function toCss(c: RGB): string {
  const b = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return `rgb(${b(c[0])}, ${b(c[1])}, ${b(c[2])})`;
}

/** `w` of `a` over `b`. w = 0 is pure b, w = 1 is pure a. */
export function mix(a: RGB, b: RGB, w: number): RGB {
  const k = w < 0 ? 0 : w > 1 ? 1 : w;
  return [a[0] * k + b[0] * (1 - k), a[1] * k + b[1] * (1 - k), a[2] * k + b[2] * (1 - k)];
}

/** WCAG relative luminance. */
export function relLuminance(c: RGB): number {
  const ch = (v: number): number => {
    const s = Math.max(0, Math.min(255, v)) / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(c[0]) + 0.7152 * ch(c[1]) + 0.0722 * ch(c[2]);
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: RGB, b: RGB): number {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Whichever candidate ink reads better on this fill.
 *
 * Not a luminance threshold — an actual comparison of the two ratios. A fixed
 * "light background gets dark text" rule is right in Day and wrong in Night,
 * where the fill is a dark red and the theme's dark ink is invisible on it. The
 * table has both inks to hand at draw time, so it can simply measure.
 */
export function readableInk(fill: RGB, inks: readonly RGB[]): RGB {
  let best = inks[0];
  let bestRatio = -1;
  for (const ink of inks) {
    const r = contrastRatio(fill, ink);
    if (r > bestRatio) {
      bestRatio = r;
      best = ink;
    }
  }
  return best;
}

/** The contrast a printed number must keep against its own cell fill. WCAG AA
 *  for body text; the digits ARE the data, so this is not negotiable. */
export const CONTRAST_TARGET = 4.5;

/**
 * How far the whole heatmap has to be pulled back, in this theme, for every
 * number to stay legible on it. 1 means the weights above are used as written.
 *
 * Day has white paper and near-black ink, so a fill can be laid on at full
 * weight and the digits still clear AA comfortably. Dusk and Night are ECDIS
 * bridge themes and are deliberately low-contrast to start with — a light-grey
 * ink on a dark-grey ground has a real but finite margin, and a fill laid over
 * that ground lifts its luminance and spends the margin. Measured on the Dusk
 * palette, the weights above dropped the worst cell to 3.4:1.
 *
 * The alternatives were worse. Brightening the ink fights the theme's whole
 * purpose (night vision on a dark bridge). Hard-coding a second weight table per
 * theme would be three tables to keep in step with any palette change, and would
 * silently be wrong the first time someone adjusts a token. So the table
 * measures instead: it searches for the largest scale at which EVERY grade
 * still clears the target, and uses that.
 *
 * One scale for all grades rather than per-grade fitting, deliberately: fitting
 * each band separately would pull the heavy No-go weight down further than the
 * light Go weight and could invert the severity ordering, so a worse hour would
 * read as a paler cell.
 */
export function fitWeightScale(
  /** Every (colour, heaviest weight) pair the table can actually paint. */
  fills: readonly (readonly [RGB, number])[],
  surface: RGB,
  inks: readonly RGB[],
  target = CONTRAST_TARGET,
  steps = 20,
): number {
  for (let s = steps; s >= 1; s--) {
    const scale = s / steps;
    let ok = true;
    for (const [color, weight] of fills) {
      const fill = mix(color, surface, weight * scale);
      if (contrastRatio(fill, readableInk(fill, inks)) < target) {
        ok = false;
        break;
      }
    }
    if (ok) return scale;
  }
  // Even the faintest wash fails: the theme has no headroom at all. Draw no
  // fill rather than an unreadable one — the number and the row label still
  // say everything the cell has to say.
  return 0;
}

/**
 * The fill for a graded cell: the grade's theme colour composited over the
 * surface at that grade's weight, pulled back by the theme's legibility scale.
 * `gradeIndex` is the position in WORK_GRADES.
 */
export function gradeFill(gradeIndex: number, gradeColor: RGB, surface: RGB, scale = 1): RGB {
  const w = (GRADE_WEIGHTS[gradeIndex] ?? GRADE_WEIGHTS[GRADE_WEIGHTS.length - 1]) * scale;
  return mix(gradeColor, surface, w);
}

/**
 * The fill for an ungraded cell on the absolute scale: the row's value against
 * the row's own maximum, as ink over the surface.
 *
 * `min` is the ramp's floor value, not zero, so a sea-temperature row spanning
 * 27–29 °C uses the whole ramp instead of three indistinguishable cells near
 * the top of a 0–29 scale.
 */
export function neutralFill(
  value: number,
  min: number,
  max: number,
  ink: RGB,
  surface: RGB,
  scale = 1,
): RGB {
  const span = max - min;
  const f = span > 0 ? (value - min) / span : 0;
  const clamped = f < 0 ? 0 : f > 1 ? 1 : f;
  const w = (NEUTRAL_WEIGHT.min + (NEUTRAL_WEIGHT.max - NEUTRAL_WEIGHT.min) * clamped) * scale;
  return mix(ink, surface, w);
}

/**
 * A row's ceiling: the largest finite value across the given series, never
 * below `floor`. The floor keeps a calm week honest — a 0.4 m sea ramped to the
 * darkest ink in its row would read as weather.
 *
 * (Salvaged verbatim from the retired conditions strip's striplayout.ts, where
 * it did the same job for the strip's bar heights.)
 */
export function rowScale(
  floor: number,
  ...series: (readonly (number | null)[] | null)[]
): number {
  let max = floor;
  for (const s of series) {
    if (s === null) continue;
    for (const v of s) {
      if (v !== null && Number.isFinite(v) && v > max) max = v;
    }
  }
  return max;
}

/** A row's floor, for the absolute ramp's low end. Mirrors rowScale. */
export function rowFloor(
  ceiling: number,
  ...series: (readonly (number | null)[] | null)[]
): number {
  let min = ceiling;
  for (const s of series) {
    if (s === null) continue;
    for (const v of s) {
      if (v !== null && Number.isFinite(v) && v < min) min = v;
    }
  }
  return min;
}

/** Grade count, so the weights table can be pinned to the band table. */
export const GRADE_COUNT = WORK_GRADES.length;
